/**
 * @file Affiche l'annonce personnalisée du restaurant dans le parcours de réservation.
 * @module packages.common.components.table-reservation.components.AnnouncementBanner
 *
 * Source unique du rendu de l'annonce : le widget et l'aperçu des réglages du
 * backoffice utilisent ce même composant, pour que l'aperçu montre exactement
 * ce que voit le client. L'icône est dans le flux, à gauche du texte : elle ne
 * peut jamais recouvrir le texte, quelle que soit la largeur.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {alpha} from '@mui/material/styles';
import {Box, Typography} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CelebrationIcon from '@mui/icons-material/Celebration';

/**
 * Obtient l'icône correspondant au style d'annonce
 *
 * @param {string} style - Le style de l'annonce
 * @return {JSX.Element} L'icône React correspondante
 */
const getAnnouncementIcon = (style) => {
  const iconProps = {sx: {fontSize: '14px'}};

  switch (style) {
    case 'warning':
      return <WarningIcon {...iconProps} />;
    case 'special':
      return <CelebrationIcon {...iconProps} />;
    case 'info':
    default:
      return <InfoIcon {...iconProps} />;
  }
};

/**
 * Obtient la couleur correspondant au style d'annonce
 *
 * @param {string} style - Le style de l'annonce
 * @param {Object} theme - Thème MUI
 * @return {string} Couleur principale pour le style
 */
const getStyleColor = (style, theme) => {
  switch (style) {
    case 'warning':
      return theme.palette.warning.main;
    case 'special':
      return theme.palette.success.main;
    case 'info':
    default:
      return theme.palette.info.main;
  }
};

/**
 * Composant d'affichage de l'annonce personnalisée
 *
 * Affiche une bannière si une annonce active existe dans la configuration restaurant.
 *
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.announcement - Configuration de l'annonce
 * @param {boolean} props.announcement.isActive - Si l'annonce est active
 * @param {string} props.announcement.text - Texte de l'annonce
 * @param {string} props.announcement.style - Style de l'annonce ('info', 'warning', 'special')
 * @return {JSX.Element|null} Bannière d'annonce ou null si inactive
 */
export default function AnnouncementBanner({announcement}) {
  // Ne rien afficher si pas d'annonce ou si inactive ou si pas de texte
  if (!announcement || !announcement.isActive || !announcement.text) {
    return null;
  }

  const icon = getAnnouncementIcon(announcement.style);

  // Tronquer le texte à 150 caractères maximum (sécurité)
  const displayText = announcement.text.length > 150 ?
    announcement.text.substring(0, 150) + '...' :
    announcement.text;

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        mb: 2,
        py: 1.5,
        px: 2,
        borderRadius: 1,
        border: `1px solid ${alpha(getStyleColor(announcement.style, theme), 0.3)}`,
        backgroundColor: alpha(getStyleColor(announcement.style, theme), 0.05),
      })}
    >
      <Box
        aria-hidden="true"
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: getStyleColor(announcement.style, theme),
          color: theme.palette.common.white,
        })}
      >
        {icon}
      </Box>

      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          lineHeight: 1.5,
          color: 'text.primary',
          fontWeight: 600,
          overflowWrap: 'anywhere',
        }}
      >
        {displayText}
      </Typography>
    </Box>
  );
}

AnnouncementBanner.propTypes = {
  announcement: PropTypes.shape({
    isActive: PropTypes.bool,
    text: PropTypes.string,
    style: PropTypes.oneOf(['info', 'warning', 'special']),
  }),
};
